import { Option } from '@spike/model';
import moment, { Moment } from 'moment-timezone';

interface ParsedAgeOption {
    months?: number;
    isPlus: boolean;
}

export interface PetAgeParts {
    years: number;
    months: number;
    totalMonths: number;
}

const monthPattern = /^(\d+)_months?$/;
const yearPattern = /^(\d+)_years?$/;
const yearMonthPattern = /^(\d+)_years?_(\d+)_months?$/;
const plusYearPattern = /^plus_(\d+)_years?$/;
const lessYearPattern = /^less_(\d+)_years?$/;

const parseAgeOption = (ageOptionId: string): ParsedAgeOption => {
    const normalized = ageOptionId.trim().toLowerCase();

    const yearMonthMatch = normalized.match(yearMonthPattern);
    if (yearMonthMatch) {
        return {
            months: Number(yearMonthMatch[1]) * 12 + Number(yearMonthMatch[2]),
            isPlus: false
        };
    }

    const yearMatch = normalized.match(yearPattern);
    if (yearMatch) {
        return {
            months: Number(yearMatch[1]) * 12,
            isPlus: false
        };
    }

    const monthMatch = normalized.match(monthPattern);
    if (monthMatch) {
        return {
            months: Number(monthMatch[1]),
            isPlus: false
        };
    }

    const plusYearMatch = normalized.match(plusYearPattern);
    if (plusYearMatch) {
        return {
            months: Number(plusYearMatch[1]) * 12,
            isPlus: true
        };
    }

    const lessYearMatch = normalized.match(lessYearPattern);
    if (lessYearMatch) {
        return {
            months: Number(lessYearMatch[1]) * 12 - 1,
            isPlus: false
        };
    }

    return { isPlus: false };
};

export const getEstimatedBirthdateFromAgeOption = (
    ageOptionId: string | undefined,
    timeZone: string
): Moment | undefined => {
    if (!ageOptionId) {
        return undefined;
    }

    const parsed = parseAgeOption(ageOptionId);
    if (parsed.months === undefined || parsed.months < 0) {
        return undefined;
    }

    const years = Math.floor(parsed.months / 12);
    const months = parsed.months % 12;

    return getEstimatedBirthdateFromAgeValues(years, months, timeZone);
};

export const getBirthdateWithAgeFallback = (
    birthdate: Moment | undefined,
    ageOptionId: string | undefined,
    timeZone: string
): Moment | undefined => {
    if (birthdate) {
        return birthdate;
    }

    return getEstimatedBirthdateFromAgeOption(ageOptionId, timeZone);
};

export const getEstimatedBirthdateFromAgeValues = (
    years: number,
    months: number,
    timeZone: string
): Moment | undefined => {
    if (years < 0 || months < 0 || months > 11) {
        return undefined;
    }

    const totalMonths = years * 12 + months;
    if (totalMonths === 0) {
        return undefined;
    }

    return moment().tz(timeZone).startOf('month').subtract(totalMonths, 'months').startOf('day');
};

export const getPetAgePartsFromBirthdate = (
    birthdate: Moment | undefined,
    timeZone: string
): PetAgeParts | undefined => {
    if (!birthdate) {
        return undefined;
    }

    const now = moment().tz(timeZone).startOf('day');
    const normalizedBirthdate = birthdate.clone().tz(timeZone).startOf('day');

    if (normalizedBirthdate.isAfter(now)) {
        return undefined;
    }

    const totalMonths = Math.max(0, now.diff(normalizedBirthdate, 'months'));

    return {
        years: Math.floor(totalMonths / 12),
        months: totalMonths % 12,
        totalMonths
    };
};

export const formatPetAgeFromBirthdate = (birthdate: Moment | undefined, timeZone: string): string => {
    const ageParts = getPetAgePartsFromBirthdate(birthdate, timeZone);

    if (!ageParts) {
        return '';
    }

    if (ageParts.totalMonths === 0) {
        return '0 months';
    }

    const yearText = ageParts.years > 0 ? `${ageParts.years} ${ageParts.years === 1 ? 'year' : 'years'}` : undefined;
    const monthText =
        ageParts.months > 0 ? `${ageParts.months} ${ageParts.months === 1 ? 'month' : 'months'}` : undefined;

    return [yearText, monthText].filter(Boolean).join(' and ');
};

export const getAgeOptionFromBirthdate = (
    birthdate: Moment | undefined,
    ageOptions: Array<Option<string>>,
    timeZone: string
): Option<string> | undefined => {
    const ageParts = getPetAgePartsFromBirthdate(birthdate, timeZone);

    if (!ageParts) {
        return undefined;
    }

    const options = ageOptions
        .map(option => {
            const parsed = parseAgeOption(option.id);
            return {
                option,
                months: parsed.months,
                isPlus: parsed.isPlus
            };
        })
        .filter(option => option.months !== undefined) as Array<{
        option: Option<string>;
        months: number;
        isPlus: boolean;
    }>;

    if (options.length === 0) {
        return undefined;
    }

    const exactMatch = options.find(option => !option.isPlus && option.months === ageParts.totalMonths);
    if (exactMatch) {
        return exactMatch.option;
    }

    const finiteOptions = options
        .filter(option => !option.isPlus)
        .sort((first, second) => first.months - second.months);

    const plusOption = options.find(option => option.isPlus);

    if (finiteOptions.length === 0) {
        return plusOption?.option;
    }

    if (ageParts.totalMonths <= finiteOptions[0].months) {
        return finiteOptions[0].option;
    }

    const maxFiniteOption = finiteOptions[finiteOptions.length - 1];
    if (ageParts.totalMonths > maxFiniteOption.months) {
        return plusOption?.option || maxFiniteOption.option;
    }

    return [...finiteOptions].reverse().find(option => option.months <= ageParts.totalMonths)?.option;
};
